import { ReactNode, useEffect, useRef } from 'react';
import { ellipsizeMiddle, formatHumanDate, formatToMaxFourDigits, IconWrapper } from '@flexo/general';
import { navigateToMember, navigateToSite } from '@flexo/resolvers';
import styles from '../table.module.scss';

/**
 * Factory function that creates a getCellContent function for PaymentsTable
 * @param t - Translation function
 * @param isNavigable - Whether site names should be clickable
 * @param member - Optional member data for site payments table
 * @returns A function to generate cell content based on column type and site data
 */
export function createGetCellContent(t: any, isNavigable: boolean = true, member?: any) {
  return function getCellContent(column: any, site: any): ReactNode {
    switch (column.field) {
      case 'resident':
        if (member) {
          return (
            <>
              <div className={styles.Table__Payments__Cell__MemberName} onClick={() => navigateToMember('member', member.memberId)}>{member?.memberFirstName || ''} {member?.memberLastName || ''}</div>
              <div className={styles.Table__Payments__Cell__MemberID}>{member.memberExternalId}</div>
            </>
          );
        }
        return null;
        
      case 'site':
        if (isNavigable) {
          return (
            <>
              <div
                className={styles.Table__Payments__Cell__SiteName}
                onClick={() => navigateToSite('site', site.siteId)}
              >
                {site.siteName}
              </div>
              <div className={styles.Table__Payments__Cell__Pod}>{ellipsizeMiddle(site.pod)}</div>
            </>
          );
        } else {
          return (
            <>
              <div className={styles.Table__Payments__Cell__SiteName}>{site.siteName}</div>
              <div className={styles.Table__Payments__Cell__Pod}>{ellipsizeMiddle(site.pod)}</div>
            </>
          );
        }

      case 'invoice':
        const typeClassName = styles.Table__Payments__Cell__Type__FirstColumn;
        return (
          <>
            <div className={typeClassName}>


              {
                site?.url
                  ?
                  <a href={site.url} target="_blank" rel="noopener noreferrer">
                    {site.invoice_type && t(`widgets.tables.paymentsTable.invoiceType.${site.invoice_type}`)}{site.invoiceID} <IconWrapper iconName="download" />
                  </a>
                  :
                  <>{site.invoice_type && t(`widgets.tables.paymentsTable.invoiceType.${site.invoice_type}`)}{site.invoiceID}</>
              }
            </div>
            <div className={styles.Table__Payments__Cell__Issued}>{t(`widgets.tables.paymentsTable.issued`)}: {formatHumanDate(site.invoiceDate)}</div>
          </>
        );

      case 'amount':
        return (
          <div className={styles.Table__Payments__Cell__Amount}>
            {site.unit} {formatToMaxFourDigits(site.amount)}
          </div>
        );

      case 'paid':
        return (
          <>
            <div className={styles.Table__Payments__Cell__Paid}>
              {site.paid ? (
                <IconWrapper iconName="tick_-encircled_active_secondary" />
              ) : null}
              {site.paid ? t(`widgets.tables.paymentsTable.paid.true`) : t(`widgets.tables.paymentsTable.paid.false`)}
            </div>
            <div className={styles.Table__Payments__Cell__DueDate}>{t(`widgets.tables.paymentsTable.dueDate`)}: {formatHumanDate(site.invoiceDueDate)}</div>
          </>
        );

      default:
        return site[column.field];
    }
  };
}

// Smoothly animates between open/closed using measured height
export function Collapsible({ open, children }: { open: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onEnd = () => {
      // After expanding, remove maxHeight to allow content to grow naturally
      if (open) el.style.maxHeight = 'none';
      el.removeEventListener('transitionend', onEnd);
    };

    if (open) {
      // Prepare from 0 to measured height
      el.style.transition = 'none';
      el.style.maxHeight = '0px';
      // Force reflow to apply the starting point
      void el.getBoundingClientRect();
      el.style.transition = 'max-height 380ms cubic-bezier(0.22, 1, 0.36, 1)';
      el.style.maxHeight = `${el.scrollHeight}px`;
      el.addEventListener('transitionend', onEnd);
    } else {
      // If currently auto-height, peg it to current height then animate to 0
      if (el.style.maxHeight === '' || el.style.maxHeight === 'none') {
        el.style.maxHeight = `${el.scrollHeight}px`;
        void el.getBoundingClientRect();
      }
      el.style.transition = 'max-height 320ms cubic-bezier(0.22, 1, 0.36, 1)';
      el.style.maxHeight = '0px';
    }
  }, [open]);

  return (
    <div ref={ref} className={styles.Table__Payments__Collapsible}>
      {children}
    </div>
  );
}